import { NormalizedNumber } from '@sparkdotfi/common-universal'
import BigNumber from 'bignumber.js'
import { UseFormReturn } from 'react-hook-form'
import { z } from 'zod'

import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { AssetInputSchema, normalizeDialogFormValues } from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog } from '@/features/dialogs/common/types'
import { MarketInfo, Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { MarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { repayValidationIssueToMessage, validateRepay } from '@/features/sparklend/common/validation/validateRepay'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getRepayDialogFormValidator(marketInfo: MarketInfo, walletInfo: MarketWalletInfo, reserve: Reserve) {
  return AssetInputSchema.superRefine((field, ctx) => {
    const formRepayAsset = normalizeDialogFormValues(field, marketInfo)
    const debt = getUserDebtToRepay({ marketInfo, reserve, payToken: formRepayAsset.token })
    const balance = walletInfo.findWalletBalanceForToken(formRepayAsset.token)

    const validateIssue = validateRepay({
      value: formRepayAsset.value,
      asset: {
        status: reserve.status,
      },
      user: {
        debt,
        balance,
      },
    })
    if (validateIssue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: repayValidationIssueToMessage[validateIssue],
        path: ['value'],
      })
    }
  })
}

export interface GetPayerDebtForRepayParams {
  marketInfo: MarketInfo
  reserve: Reserve
  payToken: Token
}
export function getUserDebtToRepay({ marketInfo, reserve, payToken }: GetPayerDebtForRepayParams): NormalizedNumber {
  const reservePosition = marketInfo.findOnePositionByToken(reserve.token)
  const reserveDebt = reservePosition.borrowBalance

  if (reserve.token.symbol === TokenSymbol('USDS') && payToken.symbol === TokenSymbol('USDC')) {
    return reserveDebt.decimalPlaces(payToken.decimals, BigNumber.ROUND_UP)
  }

  return reserveDebt
}

export interface GetFormFieldsForRepayDialogParams {
  form: UseFormReturn<AssetInputSchema>
  marketInfo: MarketInfo
  walletInfo: MarketWalletInfo
  repayMaxValue: NormalizedNumber
}
export function getFormFieldsForRepayDialog({
  form,
  marketInfo,
  walletInfo,
  repayMaxValue,
}: GetFormFieldsForRepayDialogParams): FormFieldsForDialog {
  // eslint-disable-next-line func-style
  const changeAsset = (newSymbol: TokenSymbol): void => {
    form.setValue('symbol', newSymbol)
    form.setValue('value', '')
    form.setValue('isMaxSelected', false)
    form.clearErrors()
  }

  const { symbol, value } = form.getValues()
  const token = marketInfo.findOneBySymbol(symbol)
  const balance = walletInfo.findWalletBalanceForSymbol(symbol)

  return {
    selectedAsset: token,
    selectedAssetBalance: balance,
    value,
    changeAsset,
    maxValue: repayMaxValue,
    maxSelectedFieldName: 'isMaxSelected',
  }
}
